<template>
  <div v-if="isAppReady" id="app" :class="[
    { 'nav-hidden': $store.state.hideNav },
    { 'is-animating': isAnimating },
    { 'layout-switched': $store.state.isLayoutSwitched },
    {'live-section' : isLivePage},
    activeSubsection ? kebabCase(activeSubsection.label) : false,
  ]" @click="resetAppTimeout">
  <div class="transition-overlay" />

    <transition name="fade">
      <div v-show="!$store.state.hideNav && isBottomBarVisible" class="overlay">
        <div class="overlay__inner" />
        <div class="overlay__outer" />
        <div class="overlay__sidebar" />
      </div>
    </transition>

    <transition name="fade">
      <router-view class="router-view" />
    </transition>

    <VBackground :source="$store.getters.activeBackground" />
    <BottomBar v-show="!$store.state.hideNav" :nav-items="$store.getters.activeBottomBar" />
    <SubBottomBar :nav-items="$store.getters.activeSubBottomBar" />
    <VSlides v-show="!$store.state.hideNav" :slides="$store.getters.activeSlides" />
    <VTags :tags="$store.getters.activePageTags" />
    <LivingOptionTags :living-options="$store.getters['property/activeLivingOptionTags']" />
    <LivingOptionSubsections :subsections="$store.getters['property/activeLivingOptionSubsections']" />
    <LivingOptionProperties :properties="$store.getters['property/activeLivingOptionProperties']" />
    <PropertyModal :property="$store.getters.activePropertyModal" />
    <PropertySlider :slides="$store.getters.activePropertySlides" />
    <RightSidebar v-show="!$store.state.hideNav" :content="$store.getters['sidebar/activeSidebar']" />
    <ReferenceTagSlider :tags="referenceTags" />
    <ProductCountWidget />
    <CardGrid v-if="$store.getters.activePage && $store.getters.activePage.type === 'card-grid'" />
    <VVideos v-if="$store.getters.activePage && $store.getters.activePage.type === 'video'" />
    <VFilter class="v-filter-legacy" />

    <transition name="fast-fade">
      <IframeModal v-if="$store.state.actions.showIframeUrl" :url="$store.state.actions.showIframeUrl" />
    </transition>

    <transition name="fast-fade">
      <AssetViewer :payload="$store.state.actions.showAssetSource" :tag="$store.state.actions.showAssetTag" :virtualTourUrl="$store.state.actions.showAssetvirtualTourUrl" :meta="$store.state.actions.meta" />
    </transition>

    <transition name="fast-fade">
      <VirtualTour v-if="$store.getters['property/activeVirtualTourUrl']" :url="$store.getters['property/activeVirtualTourUrl']" />
    </transition>

    <transition name="fast-fade">
      <AccessCodePrompt v-if="$store.state.accessCodePrompt" :prompt="$store.state.accessCodePrompt" />
    </transition>
  </div>
</template>
<script>
import AccessCodePrompt from "@/components/AccessCodePrompt";
import AssetViewer from "@/components/AssetViewer";
import BottomBar from "@/components/BottomBar";
import CardGrid from "@/components/CardGrid";
import IframeModal from "@/components/IframeModal";
import LivingOptionProperties from "@/components/LivingOptionProperties";
import LivingOptionSubsections from "@/components/LivingOptionSubsections";
import LivingOptionTags from "@/components/LivingOptionTags";
import ProductCountWidget from "@/components/ProductCountWidget";
import PropertyModal from "@/components/PropertyModal";
import PropertySlider from "@/components/PropertySlider";
import ReferenceTagSlider from "@/components/ReferenceTagSlider";
import RightSidebar from "@/components/RightSidebar";
import SubBottomBar from "@/components/SubBottomBar";
import VBackground from "@/components/VBackground";
import VSlides from "@/components/VSlides";
import VirtualTour from "@/components/VirtualTour";
import VTags from "@/components/VTags";
import VVideos from "@/components/VVideos";
import VFilter from "@/components/VFilter.vue";
import initConfig from "../config";
import { getAccessCodeEntityFromRoute, shouldPromptForAccessCode } from "@/router";
import { buildLivingOptionPath, buildSubsectionPath, isLivingEntryRoute, isLivingOptionRoute } from "@/utils/livingRoute";

import interactions from "@/mixins/interactions";

import { bus } from "./main";
import { mapActions, mapGetters } from "vuex";
import _ from "lodash";
import { kebabCase } from "lodash";

// No change pls
const BASE_VIEWPORT_WIDTH = 1920;
const BASE_VIEWPORT_HEIGHT = 1080;
const BASE_FONT_SIZE = 10;

const EMBED_VIEWPORT_WIDTH = 3190;
const EMBED_VIEWPORT_HEIGHT = 1960;
const EMBED_FONT_SIZE = 20;
const DEFAULT_APP_RESET_TIMEOUT_MINUTES = 10; // fallback if unable to read config (default config also set to 10 min)

export default {
  components: {
    AccessCodePrompt,
    AssetViewer,
    BottomBar,
    CardGrid,
    IframeModal,
    LivingOptionProperties,
    LivingOptionSubsections,
    LivingOptionTags,
    ProductCountWidget,
    PropertyModal,
    PropertySlider,
    ReferenceTagSlider,
    RightSidebar,
    SubBottomBar,
    VBackground,
    VirtualTour,
    VSlides,
    VTags,
    VVideos,
    VFilter,
  },

  mixins: [interactions],

  data() {
    return {
      activeInteractiveSlideId: null,
      appTimeout: null,
      pathway: "",
      routeName: "",
      isAnimating: false,
      // Paths redirected by the skipOnlyChild* flags are excluded from back-navigation history.
      automaticChildRedirectPaths: [],
    };
  },

  computed: {
    ...mapGetters({
      activeSubsection: "property/activeSubsection",
      isLivePage: "isLivePage",
      language: "language",
      playbackId: "interactions/playbackId",
      playbackIndex: "interactions/playbackIndex",
      sessionPlayback: "interactions/sessionPlayback",
      showHeat: "interactions/showHeat",
      showPlayback: "interactions/showPlayback",
    }),

    referenceTags() {
      // guard: activePageTags may be undefined briefly during navigation
      var pageTags = this.$store.getters.activePageTags;
      if (!Array.isArray(pageTags)) pageTags = [];

      var tags = pageTags.filter(function (tag) {
        return tag && tag.type === "reference";
      });

      tags = _.orderBy(tags, ["pos.x"], ["asc"]);

      var total = tags.length;
      var isRtl = this.language && this.language.dir === "rtl";

      // assign referenceIndex (avoid this.tags, use local tags)
      tags.forEach(function (tag, index) {
        if (!tag.hideTagReference) {
          tag.referenceIndex = isRtl ? total - index : index + 1;
        }
      });

      return tags;
    },

    sortedAnimationQueue() {
      return this.$store.getters.sortedAnimationQueue;
    },

    isAppReady() {
      let isLiveSectionReady = false;

      if (typeof this.$store.state.navigation[0] !== "undefined" && typeof this.$store.getters.activeBottomBar !== "undefined" && typeof this.$store.getters.activeSubBottomBar !== "undefined" && typeof this.$store.state.property.livingOptions !== "undefined" && typeof this.$store.getters["property/activeSubsection"] !== "undefined" && typeof this.$store.getters["property/activeProperties"] !== "undefined") {
        isLiveSectionReady = true;
      }

      return this.$store.state.config && this.$store.state.pages && this.$store.state.navigation && isLiveSectionReady;
    },

    isBottomBarVisible() {
      return this.$store.state.isBottomBarVisible;
    },

    // Centralizes the redirects configured by skipOnlyChildLivingOptions and
    // skipOnlyChildSubsections so every navigation source behaves consistently.
    automaticChildRoute() {
      if (!this.isAppReady || this.$store.state.accessCodePrompt) return null;

      if (this.$store.state.newConfig.skipOnlyChildLivingOptions && isLivingEntryRoute(this.$route.name)) {
        const livingOptions = this.$store.getters["property/activeLivingEntryOptions"];

        if (livingOptions.length === 1) {
          return {
            path: buildLivingOptionPath(this.$route, livingOptions[0]._id),
            query: this.$route.query,
            hash: this.$route.hash,
          };
        }
      }

      if (this.$store.state.newConfig.skipOnlyChildSubsections && isLivingOptionRoute(this.$route.name)) {
        const subsections = this.$store.getters["property/livingOptionSubsections"];

        if (subsections.length === 1) {
          return {
            path: buildSubsectionPath(this.$route, this.$route.params.livingOptionId, subsections[0]._id),
            query: {
              ...this.$route.query,
              activeSubsectionId: subsections[0]._id,
            },
            hash: this.$route.hash,
          };
        }
      }

      return null;
    },
  },

  watch: {
    $route(to, from) {
      const automaticChildRedirectIndex = this.automaticChildRedirectPaths.indexOf(to.path);
      const isAutomaticChildRedirect = automaticChildRedirectIndex !== -1;

      if (isAutomaticChildRedirect) {
        this.automaticChildRedirectPaths.splice(automaticChildRedirectIndex, 1);
      }

      if (to.name === "rest") {
        this.$store.dispatch("announceSwarmContext", "rest");
        this.$store.commit("CLEAR_URL_HISTORY");
      } else if (from.name && !isAutomaticChildRedirect) {
        if (!this.$store.state.urlHistory.includes(to.fullPath)) {
          if (!to.query.ignoreHistory) {
            this.$store.commit("ADD_URL_TO_HISTORY", from.fullPath);
          }
        } else {
          this.$store.commit("SPLICE_HISTORY_FROM_URL", to.fullPath);
        }
      }

      this.promptForCurrentRouteIfLocked();
    },

    isAppReady(isReady) {
      if (isReady) {
        this.promptForCurrentRouteIfLocked();
      }
    },

    automaticChildRoute(route) {
      if (route) {
        this.redirectToAutomaticChild(route);
      }
    },
  },

  async created() {
    const config = await initConfig();
    this.$store.commit("SET_NEW_CONFIG", config);

    this.fetchData();
    this.fetchMatterportAvailability();
  },

  mounted() {
    this.parseUrlParams();
    this.resetAppTimeout();

    // Debounce would probably be more performant but 🤷, throttle feels nicer
    window.addEventListener(
      "resize",
      _.throttle(() => {
        this.setBaseFontSize();
      }, 100)
    );
    this.setBaseFontSize();

    bus.$on("startAnimations", () => {
      const startNextAnimation = () => {
        if (!this.sortedAnimationQueue.length) return false;
        bus.$emit(`animate${this.sortedAnimationQueue[0]}`);
        return true;
      };

      if (startNextAnimation()) return;

      // Route updates can enqueue animations after the initial start signal.
      this.$nextTick(() => {
        if (startNextAnimation()) return;

        setTimeout(() => {
          startNextAnimation();
        }, 75);
      });
    });

    bus.$on("finishedAnimation", (component) => {
      if (this.$store.state.animations.newAnimationQueue.indexOf(component) > -1) {
        this.$store.commit("advanceAnimationQueue", component);
        this.$nextTick(() => {
          if (this.sortedAnimationQueue.length) {
            bus.$emit(`animate${this.sortedAnimationQueue[0]}`);
          } else {
            bus.$emit("finishedAllAnimations");
          }
        });
      }
    });

    setTimeout(() => {
      bus.$emit("startAnimations");
    }, 2000);
  },

  methods: {
    ...mapActions(["fetchData", "fetchMatterportAvailability"]),

    kebabCase: kebabCase,

    redirectToAutomaticChild(route) {
      if (!this.automaticChildRedirectPaths.includes(route.path)) {
        this.automaticChildRedirectPaths.push(route.path);
      }

      this.$router.replace(route).catch(() => {
        const redirectIndex = this.automaticChildRedirectPaths.indexOf(route.path);
        if (redirectIndex !== -1) {
          this.automaticChildRedirectPaths.splice(redirectIndex, 1);
        }
      });
    },

    promptForCurrentRouteIfLocked() {
      if (!this.isAppReady) return;

      const entity = getAccessCodeEntityFromRoute(this.$route);
      if (!shouldPromptForAccessCode(entity)) return;

      this.$store.commit("OPEN_ACCESS_CODE_PROMPT", {
        accessCode: entity.accessCode,
        entityId: entity._id,
        isCurrentRoute: true,
        pendingRoute: {
          path: this.$route.path,
          query: this.$route.query,
          hash: this.$route.hash,
        },
      });
    },

    // We use cookies to set some variables on a per browser level
    // This is then set as a session cookie so even refreshing to http://localhost/#/ wont lose the settings
    parseUrlParams() {
      const query = this.$route.query;
      const store = this.$store.state;

      // Define keys and whether they should be parsed as booleans
      const config = [
        { key: "deviceName", isBoolean: false }, // Name of the NUC running this instance (PROJECT-1), used for snitching
        { key: "videoswarmHost", isBoolean: false }, // Hostname of videoswarm host device (http://localhost:3000)
        { key: "videowallHost", isBoolean: false }, // Hostname of videowall host device (http://localhost:3000)
        { key: "navSocketHost", isBoolean: false }, // Hostname of the Navigation Socket Server (http://localhost:8888)
        { key: "hideLivingOptions", isBoolean: false }, // Comma-seperated list of LivingOption IDs to hide from masterplan (66f226d5f5d36e9408a61789,66f22a5af5d36e9408a6179e)
        { key: "hideNavItems", isBoolean: false }, // Comma-seperated list of NavigationItem IDs to hide from sub-navigation (66f226d5f5d36e9408a61789,66f22a5af5d36e9408a6179e)
        { key: "selector", isBoolean: false }, // URL that the "Explore Communities" navigates to (http://localhost:9000)
        { key: "homeUrl", isBoolean: false }, // URL that the "Home"" button navigates to (http://localhost:9001/category/66f22a5af5d36e9408a6179e/page/66f226d5f5d36e9408a61789)
        { key: "navMaster", isBoolean: true }, // Use this instance as a Navigation Controller Master, usually on a tablet device (true/false)
        { key: "navSlave", isBoolean: true }, // Use this instance as a Navigation Controller Slave, usually on a videowall device (true/false)
        { key: "hideNav", isBoolean: true }, // Hide BottomBar, SubBottomBar and SideBarm, usually on a videowall device (true/false)
      ];

      config.forEach(({ key }) => {
        if (typeof query[key] !== "undefined") {
          localStorage.setItem(key, query[key]);
        }
      });

      // Load from localStorage into store
      config.forEach(({ key, isBoolean }) => {
        const stored = localStorage.getItem(key);
        if (stored !== null) {
          store[key] = isBoolean ? stored === "true" : stored;
        }
      });

      // Check navMaster and navSlave mutual exclusion
      if (store.navMaster && store.navSlave) {
        console.warn("Cant be both navMaster and navSlave. Deactivating nav control support.");
        store.navMaster = false;
        store.navSlave = false;
      }
    },

    resetAppTimeout() {
      if (this.appTimeout) {
        clearInterval(this.appTimeout);
      }

      if (!this.$store.state.navSlave && !this.$store.state.navMaster) {
        const configuredTimeoutMinutes = Number(this.$store.state.newConfig.general && this.$store.state.newConfig.general.appResetTimeoutMinutes);
        const appResetTimeoutMinutes = Number.isFinite(configuredTimeoutMinutes) && configuredTimeoutMinutes > 0 ? configuredTimeoutMinutes : DEFAULT_APP_RESET_TIMEOUT_MINUTES;

        this.appTimeout = setInterval(() => {
          this.resetInteractionSession();
          if (this.$store.state.homeUrl) {
            // disable until we sort out videoswarm
            // setTimeout(() => {
            //   window.location.href = this.$store.state.homeUrl
            // }, 100)
          } else {
            this.$store.dispatch("triggerVideo", "default");
            this.$store.dispatch("announceSwarmContext", "rest");
            window.location.href = window.location.origin;
          }
        }, 1000 * 60 * appResetTimeoutMinutes);
      }
    },

    // Super important: The base font size is used to position and size everything using REM at different screen sizes
    setBaseFontSize() {
      const widthScale = window.innerWidth / (this.$store.state.hideNav ? EMBED_VIEWPORT_WIDTH : BASE_VIEWPORT_WIDTH);
      const heightScale = window.innerHeight / (this.$store.state.hideNav ? EMBED_VIEWPORT_HEIGHT : BASE_VIEWPORT_HEIGHT);

      if (widthScale < heightScale) {
        this.$store.state.appScale = widthScale;
      } else {
        this.$store.state.appScale = heightScale;
      }

      document.documentElement.style.fontSize = `${this.$store.state.appScale * (this.$store.state.hideNav ? EMBED_FONT_SIZE : BASE_FONT_SIZE)}px`;
    },

    isObject(item) {
      return item && typeof item === "object" && !Array.isArray(item);
    },

    mergeDeep(target, ...sources) {
      if (!sources.length) return target;
      const source = sources.shift();

      if (this.isObject(target) && this.isObject(source)) {
        for (const key in source) {
          if (this.isObject(source[key])) {
            if (!target[key]) Object.assign(target, { [key]: {} });
            this.mergeDeep(target[key], source[key]);
          } else {
            Object.assign(target, { [key]: source[key] });
          }
        }
      }

      return this.mergeDeep(target, ...sources);
    },
  },
};
</script>

<style lang="scss">
html {
  background-color: #333;
  color: #fff;
  color-scheme: only light;
  font-size: 62.5%;
  font-weight: var(--font-weight-secondary);
  font-style: normal;
  -webkit-font-smoothing: antialiased;
  overflow: hidden;
}

body {
  display: flex;
  height: 100dvh;
  margin: 0;
}

#app {
  background-color: #333;
  font-family: var(--font-family-secondary);
  height: 108rem;
  margin: auto;
  overflow: hidden;
  pointer-events: auto !important;
  position: relative;
  width: 192rem;

  &.nav-hidden {
    height: 98rem;
    width: 159.5rem;
  }
}

* {
  box-sizing: border-box;
  outline: none;
  pointer-events: none;
  user-select: none;
  -webkit-font-smoothing: subpixel-antialiased;
  cursor: url("/assets/cursor-6.png") 8 8, auto;
}

iframe {
  pointer-events: auto;
  user-select: auto;
}

.touch {
  pointer-events: auto !important;

  .is-animating & {
    pointer-events: none !important;
  }
}

a {
  color: #fff;
  text-decoration: none;
}

p {
  margin-bottom: 0.6em;
  margin-top: 0;
}

button {
  border: none;
  background-color: transparent;
  color: #fff;
  font-family: var(--font-family-secondary);
  letter-spacing: 0.05em;
}

.main {
  height: 98rem;
  left: 0;
  position: absolute;
  top: 0;
  width: 159.5rem;
}

.transition-overlay {
  animation: overlay-reveal 1s forwards 1s;
  background: #000;
  bottom: 0;
  left: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 10000;
}

.overlay {
  background: transparent;
  height: 100%;
  pointer-events: none;
  position: relative;
  z-index: 1000;

  &__outer {
    &::before,
    &::after {
      bottom: 0;
      content: "";
      opacity: 0.5;
      position: absolute;
      top: 0;
      width: var(--overlay-gradient-width);
    }

    &::before {
      background: linear-gradient(90deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.738) 19%, rgba(0, 0, 0, 0.541) 34%, rgba(0, 0, 0, 0.382) 47%, rgba(0, 0, 0, 0.278) 56.5%, rgba(0, 0, 0, 0.194) 65%, rgba(0, 0, 0, 0.126) 73%, rgba(0, 0, 0, 0.075) 80.2%, rgba(0, 0, 0, 0.042) 86.1%, rgba(0, 0, 0, 0.021) 91%, rgba(0, 0, 0, 0.008) 95.2%, rgba(0, 0, 0, 0.002) 98.2%, rgba(0, 0, 0, 0) 100%);
      left: 0;
    }

    &::after {
      background: linear-gradient(270deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.738) 19%, rgba(0, 0, 0, 0.541) 34%, rgba(0, 0, 0, 0.382) 47%, rgba(0, 0, 0, 0.278) 56.5%, rgba(0, 0, 0, 0.194) 65%, rgba(0, 0, 0, 0.126) 73%, rgba(0, 0, 0, 0.075) 80.2%, rgba(0, 0, 0, 0.042) 86.1%, rgba(0, 0, 0, 0.021) 91%, rgba(0, 0, 0, 0.008) 95.2%, rgba(0, 0, 0, 0.002) 98.2%, rgba(0, 0, 0, 0) 100%);
      right: 0;
    }
  }

  &__inner {
    &::after {
      background: linear-gradient(270deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.738) 19%, rgba(0, 0, 0, 0.541) 34%, rgba(0, 0, 0, 0.382) 47%, rgba(0, 0, 0, 0.278) 56.5%, rgba(0, 0, 0, 0.194) 65%, rgba(0, 0, 0, 0.126) 73%, rgba(0, 0, 0, 0.075) 80.2%, rgba(0, 0, 0, 0.042) 86.1%, rgba(0, 0, 0, 0.021) 91%, rgba(0, 0, 0, 0.008) 95.2%, rgba(0, 0, 0, 0.002) 98.2%, rgba(0, 0, 0, 0) 100%);
      bottom: 10rem;
      content: "";
      opacity: 0.5;
      position: absolute;
      right: 32.5rem;
      top: 0;
      width: var(--overlay-gradient-width);

      .layout-switched & {
        bottom: 0;
        top: 10rem;
      }
    }
  }

  &__sidebar {
    &::before {
      background: linear-gradient(90deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.738) 19%, rgba(0, 0, 0, 0.541) 34%, rgba(0, 0, 0, 0.382) 47%, rgba(0, 0, 0, 0.278) 56.5%, rgba(0, 0, 0, 0.194) 65%, rgba(0, 0, 0, 0.126) 73%, rgba(0, 0, 0, 0.075) 80.2%, rgba(0, 0, 0, 0.042) 86.1%, rgba(0, 0, 0, 0.021) 91%, rgba(0, 0, 0, 0.008) 95.2%, rgba(0, 0, 0, 0.002) 98.2%, rgba(0, 0, 0, 0) 100%);
      bottom: 10rem;
      content: "";
      opacity: 0.5;
      position: absolute;
      right: 30rem;
      top: 0;
      width: var(--overlay-gradient-width);

      .layout-switched & {
        bottom: 0;
        top: 10rem;
      }
    }
  }
}

.interaction {
  animation: interaction 1.2s forwards;
  opacity: 0.8;
  pointer-events: none;
  position: fixed;
  transform-origin: 0% 0%;
  z-index: 20000;

  &::after {
    content: "";
    background: hotpink;
    border-radius: 50%;
    display: block;
    height: 4rem;
    transform: translate(-50%, -50%);
    width: 4rem;
  }
}

@keyframes interaction {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  10% {
    opacity: 1;
    transform: scale(0.5);
  }
  45% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: scale(1.1);
  }
}

.interaction-drag {
  animation: interaction-drag 2s forwards;
  opacity: 1;
  pointer-events: none;
  position: fixed;
  transform-origin: 0% 0%;
  z-index: 20000;

  &::after {
    content: "";
    background: greenyellow;
    border-radius: 50%;
    display: block;
    height: 1rem;
    transform: translate(-50%, -50%);
    width: 1rem;
  }
}

@keyframes interaction-drag {
  50% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0);
  }
}
</style>
